// Category normalization + practitioner-specialty mapping.

// Maps a practitioner's specialty label to the service category it
// should filter against in the main picker.
export const SPECIALTY_CATEGORY_MAP = {
  'Massage Therapist': 'Massage Therapy',
  'Acupuncturist': 'Acupuncture',
  'Esthetician': 'Skin Care',
};

// Built-in specialties. Used when the core doc has no `specialties`
// array yet (pre-feature data) so existing practitioners keep their
// category mapping + colors without a migration step.
export const DEFAULT_SPECIALTIES = [
  { name: 'Massage Therapist', category: 'Massage Therapy', color: '#66c795' },
  { name: 'Acupuncturist', category: 'Acupuncture', color: '#11b7ee' },
  { name: 'Esthetician', category: 'Skin Care', color: '#f5b5f7' },
];

// Normalize raw `specialties` from Firestore into a uniform
// { name, category, color } shape. Falls back to DEFAULT_SPECIALTIES
// when the field is missing or empty.
export const processSpecialtiesData = (rawSpecialties, defaultColor = '#314e4a') => {
  if (Array.isArray(rawSpecialties) && rawSpecialties.length > 0) {
    return rawSpecialties
      .filter(sp => sp && (typeof sp === 'string' || sp.name))
      .map(sp => (typeof sp === 'string'
        ? { name: sp, category: SPECIALTY_CATEGORY_MAP[sp] || '', color: defaultColor }
        : { name: sp.name, category: sp.category || SPECIALTY_CATEGORY_MAP[sp.name] || '', color: sp.color || defaultColor }));
  }
  return DEFAULT_SPECIALTIES.map(sp => ({ ...sp }));
};

// Resolve the service category a practitioner specialty maps to.
// Checks the admin-managed list first, then the legacy hardcoded map.
export const getSpecialtyCategory = (specialties, specialtyName) => {
  if (!specialtyName) return '';
  const match = (specialties || []).find(sp => sp.name === specialtyName);
  if (match && match.category) return match.category;
  return SPECIALTY_CATEGORY_MAP[specialtyName] || '';
};

// Resolve the swatch color for a specialty (undefined when unknown so
// callers can keep their own fallback styling).
export const getSpecialtyColor = (specialties, specialtyName) => {
  if (!specialtyName) return undefined;
  const match = (specialties || []).find(sp => sp.name === specialtyName);
  if (match && match.color) return match.color;
  const builtIn = DEFAULT_SPECIALTIES.find(sp => sp.name === specialtyName);
  return builtIn ? builtIn.color : undefined;
};

// Legacy behavior before the per-category `taxable` flag existed:
// Acupuncture was the only non-taxable category. Categories saved
// without the flag keep that behavior.
export const defaultTaxableFor = (name) => name !== 'Acupuncture';

// Normalize raw category data from Firestore into a uniform
// { name, color, taxable } shape. Legacy docs may store categories as
// plain strings or as objects missing a color / taxable flag; we fall
// back to the default swatch and legacy tax rule so the UI always has
// something to render.
//
// If categories are missing entirely, synthesize them from the distinct
// `category` values found on the services list.
export const processCategoriesData = (rawCategories, services, defaultColor = '#314e4a') => {
  if (Array.isArray(rawCategories)) {
    return rawCategories.map(cat =>
      (typeof cat === 'object' && cat !== null && cat.name)
        ? { name: cat.name, color: cat.color || defaultColor, taxable: typeof cat.taxable === 'boolean' ? cat.taxable : defaultTaxableFor(cat.name) }
        : { name: cat, color: defaultColor, taxable: defaultTaxableFor(cat) }
    );
  }
  const serviceCategories = services && Array.isArray(services)
    ? [...new Set(services.map(s => s.category).filter(Boolean))]
    : [];
  return serviceCategories.map(catName => ({ name: catName, color: defaultColor, taxable: defaultTaxableFor(catName) }));
};

// Services historically stored "currently available" under a
// per-category field. New / custom categories use the generic field.
// Both helpers route to the right one so the checkbox works everywhere.
export const availabilityFieldFor = (categoryName) => {
  if (categoryName === 'Massage Therapy') return 'isCurrentlyAvailableMassage';
  if (categoryName === 'Acupuncture') return 'isCurrentlyAvailableAcupuncture';
  if (categoryName === 'Skin Care') return 'isCurrentlyAvailableSkinCare';
  return 'isCurrentlyAvailable';
};

export const isServiceCurrentlyAvailable = (svc) => {
  if (!svc) return false;
  return !!svc[availabilityFieldFor(svc.category)];
};

// Whether sales tax applies to services in the named category.
export const isCategoryTaxable = (categories, categoryName) => {
  const match = (categories || []).find(c => c.name === categoryName);
  if (match && typeof match.taxable === 'boolean') return match.taxable;
  return defaultTaxableFor(categoryName);
};

// Durations a practitioner offers for a service, limited to durations that
// still exist on the service. Guards against stale timings after a service
// duration/price row is edited or removed.
export const validOfferedDurations = (osEntry, service) => {
  const offered = (osEntry && osEntry.offeredDurations) || [];
  if (!service || !Array.isArray(service.durationPrices)) return offered;
  const valid = new Set(service.durationPrices.map(dp => Number(dp.duration)));
  return offered.filter(d => valid.has(Number(d)));
};
